/**
 * Workshop event catalog — same shape a future API should return.
 *
 * Event: { title, pitch, steps[], complete?, shareFieldIds? }
 * Share image uses complete.headlineFieldId only (shareFieldIds optional legacy fallback when length === 1).
 * Step: { id, title, timeEstimate?, subtitle?, fields[] }
 * Field types: static | textarea | fill-blank | checkbox-group
 */

export const lettingGoEvent = {
    title: 'The Practice of Letting Go',
    pitch: "You're holding onto something that is holding you back.",
    complete: {
        headlineFieldId: 'release_statement',
        subFieldId: 'becoming',
    },
    shareFieldIds: ['release_statement'],
    steps: [
        {
            id: 'regulate',
            title: 'Regulate First',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content: 'Three slow breaths: inhale 4, hold 4, exhale 6, hold 2. Repeat three times.',
                },
                {
                    type: 'textarea',
                    id: 'body_check',
                    prompt: 'What does your body feel right now?',
                },
            ],
        },
        {
            id: 'name_it',
            title: 'Name It',
            timeEstimate: '10 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'freewrite',
                    prompt: 'What have I been carrying that feels heavy, draining, or outdated?',
                },
                {
                    type: 'textarea',
                    id: 'focus',
                    prompt: 'The one thing I want to focus on:',
                },
                {
                    type: 'textarea',
                    id: 'protects_from',
                    prompt: 'I hold onto this because it protects me from:',
                },
                {
                    type: 'textarea',
                    id: 'afraid_if',
                    prompt: "If I let this go, I'm afraid:",
                },
            ],
        },
        {
            id: 'understand_feel',
            title: 'Understand & Feel It',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'describe',
                    prompt: "Describe what's here in your own words:",
                },
                {
                    type: 'textarea',
                    id: 'cost',
                    prompt: 'What has holding onto this cost me in the last year?',
                },
                {
                    type: 'checkbox-group',
                    id: 'emotions',
                    prompt: 'Emotions I notice right now:',
                    options: ['Relief', 'Sadness', 'Fear', 'Gratitude', 'Resistance', 'Grief', 'Shame', 'Other'],
                },
            ],
        },
        {
            id: 'honor_it',
            title: 'Honor It',
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'what_it_protected',
                    prompt: 'At some point, this helped me. What was it protecting me from?',
                },
                {
                    type: 'fill-blank',
                    id: 'served_me',
                    prefix: 'I understand that this served me by',
                    suffix: '',
                },
            ],
        },
        {
            id: 'release',
            title: 'Release',
            subtitle: 'The rep that counts',
            fields: [
                {
                    type: 'fill-blank',
                    id: 'release_statement',
                    prefix: 'I am ready to release',
                    suffix: '',
                },
                {
                    type: 'textarea',
                    id: 'body_noticed',
                    prompt: 'What I noticed in my body when I said it aloud:',
                },
            ],
        },
        {
            id: 'step_into_next',
            title: "Step Into What's Next",
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'fill-blank',
                    id: 'becoming',
                    prefix: 'I am someone who',
                    suffix: '',
                },
                {
                    type: 'textarea',
                    id: 'new_practice',
                    prompt: 'One new behavior/practice/belief I will practice this week:',
                },
            ],
        },
    ],
};

export const connectionEvent = {
    title: 'The Practice of Connection',
pitch:
    'Practice three simple skills for stronger relationships: listening so people feel seen, following threads past small talk, and sharing short human stories.',

complete: {
    headlineFieldId: 'connection_commitment',
},

shareFieldIds: ['connection_commitment'],

steps: [
    {
        id: 'arrive',
        title: 'Arrive Together',
        timeEstimate: '2 min',
        fields: [
            {
                type: 'static',
                content:
                    'Take three slow breaths: inhale 4, hold 4, exhale 6, hold 2. Look around the room. For the next few minutes, just sit in the moment and be present.',
            },
        ],
    },

    {
        id: 'listen_belong',
        title: 'Listen for Connection',
        timeEstimate: '5 min',
        fields: [
            {
                type: 'static',
                content:
                    'Skill 1: Listen to understand. Do not try to prepare a response. Let the other person speak without interrupting, fixing, giving advice, or turning the conversation back to yourself. Your job is simply to understand their experience.',
            },
            {
                type: 'static',
                content:
                    'After they finish, reflect back what you heard. You can start with: “What I’m hearing is…” Then ask: “Did I get that right?”',
            },
            {
                type: 'textarea',
                id: 'listen_practice',
                prompt:
                    'What do you think mattered most to them in what they shared?',
            },
        ],
    },

    {
        id: 'go_deeper',
        title: 'Go Beyond Small Talk',
        timeEstimate: '5 min',
        fields: [
            {
                type: 'static',
                content:
                    'Skill 2: Follow the thread. You do not need a perfect “deep question.” Listen for something with energy, emotion, or meaning, and stay with it for one more layer.',
            },
            {
                type: 'static',
                content:
                    'Small talk usually stays with facts. Connection grows when you get curious about what the experience felt like or why it mattered. Try phrases like: “What was that like for you?”, “Why did that matter?”, or “What made that important to you?”',
            },
            {
                type: 'static',
                content:
                    'Go one layer deeper. If the person opens up, stay curious. If they pull back, respect that.',
            },
            {
                type: 'textarea',
                id: 'deeper_question',
                prompt:
                    'What is one thread you could follow deeper in the conversation?',
            },
        ],
    },

    {
        id: 'tell_stories',
        title: 'Tell Stories for Connection',
        timeEstimate: '5 min',
        fields: [
            {
                type: 'static',
                content:
                    'Skill 3: Share something real. A good connection story does not need to be dramatic or deeply personal. It just needs to help someone understand a little more about you.',
            },
            {
                type: 'static',
                content:
                    'Use this simple structure: Moment → Feeling → Meaning. What happened? What did you feel? Why did it matter to you?',
            },
            {
                type: 'static',
                content:
                    'Facts tell people what happened. Stories help people understand what the experience meant to you.',
            },
            {
                type: 'fill-blank',
                id: 'story_invite',
                prefix: 'A small story I could share is about',
                suffix: '',
            },
        ],
    },

    {
        id: 'practice_together',
        title: 'Practice Together',
        timeEstimate: '8 min',
        fields: [
            {
                type: 'static',
                content:
                    'Now combine the three skills in a real conversation. Listen fully. Notice one thread and follow it deeper. When it feels natural, share a short story of your own using Moment → Feeling → Meaning.',
            },
            {
                type: 'static',
                content:
                    'The goal is not to force a deep conversation. The goal is to practice being more present, curious, and real.',
            },
            {
                type: 'checkbox-group',
                id: 'practice_feelings',
                prompt: 'How did the conversation feel?',
                options: [
                    'Seen',
                    'Curious',
                    'Nervous',
                    'Grateful',
                    'Connected',
                    'Surprised',
                    'Other',
                ],
            },
        ],
    },

    {
        id: 'carry_forward',
        title: 'Carry It Forward',
        timeEstimate: '3 min',
        fields: [
            {
                type: 'static',
                content:
                    'Connection gets better with practice. Choose one small thing you want to practice in a conversation this week.',
            },
            {
                type: 'fill-blank',
                id: 'connection_commitment',
                prefix: 'This week, I will practice connection by',
                suffix: '',
            },
        ],
    },
],
};

export const capacityExpressionEvent = {
    title: 'The Practice of Emotional Capacity & Expression',
    pitch:
        'Name what you feel, notice it in your body, and practice saying it out loud — one rep at a time.',
    complete: {
        headlineFieldId: 'capacity_commitment',
    },
    shareFieldIds: ['capacity_commitment'],
    steps: [
        {
            id: 'arrive',
            title: 'Arrive Together',
            timeEstimate: '2 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Take three slow breaths: inhale 4, hold 4, exhale 4, hold 4. Look around the room. For the next few minutes, just sit in the moment and be present.',
                },
            ],
        },
        {
            id: 'where_now',
            title: 'Where Are You Right Now?',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Imagine two axes: energy from low to high, and mood from unpleasant to pleasant. That gives four quadrants: high-unpleasant, high-pleasant, low-unpleasant, low-pleasant. Place yourself where you are right now. Do not think too hard.',
                },
                {
                    type: 'textarea',
                    id: 'grid_self_description',
                    prompt:
                        'In a few words, where are you on energy and mood right now?',
                },
                {
                    type: 'textarea',
                    id: 'grid_quadrant',
                    prompt:
                        'Which quadrant feels closest: high-unpleasant, high-pleasant, low-unpleasant, or low-pleasant?',
                },
            ],
        },
        {
            id: 'name_feeling',
            title: 'Name the Feeling',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Pick the words that fits best. This expands your vocabulary without forcing perfect labels. Do not think too hard.',
                },
                {
                    type: 'checkbox-group',
                    id: 'feeling_word',
                    prompt: 'Which word fits best right now? (Choose all that apply.)',
                    options: [
                        'tense',
                        'irritated',
                        'anxious',
                        'overwhelmed',
                        'frantic',
                        'on edge',
                        'energized',
                        'excited',
                        'motivated',
                        'hopeful',
                        'inspired',
                        'focused',
                        'drained',
                        'sad',
                        'numb',
                        'disconnected',
                        'heavy',
                        'defeated',
                        'calm',
                        'content',
                        'peaceful',
                        'grounded',
                        'reflective',
                        'still',
                    ],
                },
                {
                    type: 'textarea',
                    id: 'feeling_custom',
                    prompt: 'If none of these fit, name it in your own words (short).',
                },
            ],
        },
        {
            id: 'body_scan',
            title: 'Where Do You Feel This?',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Feelings often show up in the body before we have words for them. This is awareness, not a diagnosis: just notice what is true for you.',
                },
                {
                    type: 'checkbox-group',
                    id: 'body_zones',
                    prompt: 'Where do you feel this in your body? Tap all that apply.',
                    options: [
                        'Chest',
                        'Stomach',
                        'Shoulders',
                        'Throat',
                        'Jaw',
                        'Head',
                        'Behind the eyes',
                        'Arms or hands',
                        'Legs or feet',
                        'Low back',
                        'Whole body',
                    ],
                },
            ],
        },
        {
            id: 'reflect_express',
            title: 'Reflect and Express',
            timeEstimate: '7 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'reflection',
                    prompt:
                        'What matters about how you are feeling right now? Two or three sentences max.',
                },
                {
                    type: 'static',
                    content:
                        'Before you continue, say this out loud once: “I am feeling [your word].” The saying-it-out-loud is the expression rep.',
                },
            ],
        },
        {
            id: 'make_sense',
            title: 'Make Sense of It',
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'High, difficult energy is often your system mobilizing around something that matters. It is not broken: it is trying to respond.',
                },
                {
                    type: 'static',
                    content:
                        'Depletion is information. It is your body asking for something it has not gotten.',
                },
                {
                    type: 'static',
                    content:
                        'When mood is pleasant and energy is lower, that can be regulation — notice it so you can find your way back.',
                },
            ],
        },
        {
            id: 'carry_forward',
            title: 'Carry It Forward',
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Emotional capacity grows with reps. Choose one small practice you will try this week.',
                },
                {
                    type: 'fill-blank',
                    id: 'capacity_commitment',
                    prefix: 'This week, I will practice emotional capacity by',
                    suffix: '',
                },
            ],
        },
    ],
};

export const mindsetAwarenessEvent = {
    title: 'The Practice of Mindset & Awareness',
    pitch:
        'Follow one thought from surface behavior to the belief underneath, then try on what you would choose instead.',
    complete: {
        headlineFieldId: 'mindset_commitment',
    },
    shareFieldIds: ['mindset_commitment'],
    steps: [
        {
            id: 'arrive',
            title: 'Arrive Together',
            timeEstimate: '2 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Take three slow breaths: inhale 4, hold 4, exhale 4, hold 4. Look around the room. For the next few minutes, just sit in the moment and be present.',
                },
            ],
        },
        {
            id: 'surface',
            title: "What's On Your Mind?",
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'It could be a thought, a situation, a decision, a feeling, or a pattern. Do not overthink the category, just name what is there.',
                },
                {
                    type: 'textarea',
                    id: 'surface_thought',
                    prompt:
                        'What is on your mind that you keep coming back to?',
                },
                {
                    type: 'static',
                    content:
                        'Examples: “I keep saying yes when I mean no” · “I cannot stop thinking about that conversation” · “I do not know why I keep starting things I do not finish”. Write the version that is actually in your head. Do not clean it up.',
                },
            ],
        },
        {
            id: 'behavior',
            title: 'The Behavior',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Not what you wish you did. What you actually do: avoid it, push through, get quiet, get busy, pick a fight. Two or three sentences max.',
                },
                {
                    type: 'textarea',
                    id: 'usual_behavior',
                    prompt: 'When this comes up, what do you usually do?',
                },
            ],
        },
        {
            id: 'protection',
            title: 'The Protection Layer',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'It might protect you from disappointment, from trying, from needing people, from being seen.',
                },
                {
                    type: 'textarea',
                    id: 'protection_layer',
                    prompt: 'What does this thought or pattern protect you from?',
                },
            ],
        },
        {
            id: 'excavation',
            title: 'Excavation',
            timeEstimate: '10 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'excavation_belief',
                    prompt:
                        'Belief: What does that tell you about what you believe: about yourself, about people, about how the world works?',
                },
                {
                    type: 'textarea',
                    id: 'excavation_value',
                    prompt:
                        'Value: What does that belief say about what you think you have to earn or prove?',
                },
                {
                    type: 'textarea',
                    id: 'excavation_origin',
                    prompt: 'Origin: Where did you first learn that?',
                },
                {
                    type: 'textarea',
                    id: 'excavation_identity',
                    prompt: 'Identity: What kind of person does believing this make you?',
                },
                {
                    type: 'static',
                    content: 'One more.',
                },
                {
                    type: 'textarea',
                    id: 'excavation_inflection',
                    prompt:
                        'The inflection: Is that who you are, or just who you learned to be?',
                },
            ],
        },
        {
            id: 'new_belief',
            title: 'Try On a New Belief',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'It does not have to feel true yet. You are not committing to it. You are just trying it on, one sentence.',
                },
                {
                    type: 'textarea',
                    id: 'new_belief',
                    prompt:
                        'What would you need to believe about yourself for this to shift?',
                },
            ],
        },
        {
            id: 'your_arc',
            title: 'Your Arc',
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Look back at what you wrote: what you are carrying, what it protects, where it came from, who it made you, and who you are choosing instead.',
                },
                {
                    type: 'static',
                    content:
                        'You came in carrying something heavy. You leave with a belief you are trying on. That distance is the rep.',
                },
            ],
        },
        {
            id: 'carry_forward',
            title: 'Carry It Forward',
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'static',
                    content:
                        'Mindset shifts with practice. Choose one small thing you will try this week.',
                },
                {
                    type: 'fill-blank',
                    id: 'mindset_commitment',
                    prefix: 'This week, I will practice building my mindset and awareness by:',
                    suffix: '',
                },
            ],
        },
    ],
};

export const EVENT_CATALOG = {
    'letting-go-sep2026': lettingGoEvent,
    'letting-go-oct2026': lettingGoEvent,
    'connection-oct-2026': connectionEvent,
    'capacity-expression-oct-2026': capacityExpressionEvent,
    'mindset-awareness-oct-2026': mindsetAwarenessEvent,
};

export function normalizeEventCode(raw) {
    return String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
}

export function getEventByCode(rawCode) {
    const eventCode = normalizeEventCode(rawCode);
    if (eventCode && EVENT_CATALOG[eventCode]) {
        return { eventCode, event: EVENT_CATALOG[eventCode], found: true };
    }
    return { eventCode, event: null, found: false };
}

/** Later: fetch from API by code, fallback to EVENT_CATALOG. */
export async function resolveEvent(code) {
    return getEventByCode(code);
}

export const DEFAULT_EVENT_CODE = 'letting-go-oct2026';
